import React, { useState, useMemo } from 'react';
import Select from 'react-select';
import { useData } from '../contexts/DataContext';
import { useTranslation } from 'react-i18next';

function Indicators({ showToast }) {
  const { t } = useTranslation();
  const { dataSources } = useData();
  // View and filtering states
  const [activeTab, setActiveTab] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [editingIndicator, setEditingIndicator] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedCollectTypes, setSelectedCollectTypes] = useState([]);
  const [selectedStatuses, setSelectedStatuses] = useState(['active']); // Show active by default
  const [viewMode, setViewMode] = useState('grid'); // 'grid', 'list', 'table'
  const [showQuickViewModal, setShowQuickViewModal] = useState(false);
  const [quickViewIndicator, setQuickViewIndicator] = useState(null);

  // Helper function to infer field type from sample value
  const inferFieldType = (value) => {
    if (value === null || value === undefined) return 'text';
    if (typeof value === 'boolean') return 'boolean';
    if (typeof value === 'number') return 'number';
    // Check for date patterns
    if (typeof value === 'string') {
      const datePattern = /^\d{4}-\d{2}-\d{2}/;
      if (datePattern.test(value)) return 'date';
    }
    return 'text';
  };

  // Map uploaded data sources to availableDataSources format
  const availableDataSources = useMemo(() => {
    const uploadedFiles = dataSources.filter(ds => ds.type === 'CSV' || ds.type === 'Excel');
    
    // Keep mock sources for backward compatibility with existing indicators
    const mockSources = [
      { 
        id: 'mock-1', 
        code: 'CORE_BANKING', 
        name: 'Core Banking System (Mock)',
        description: t('indicators.mockData.coreBanking.description'),
        tables: [
          { 
            id: 'loans', 
            name: 'Loans', 
            fields: [
              { name: 'loan_id', type: 'number', label: 'Loan ID', description: t('indicators.mockData.fields.loanId') },
              { name: 'amount', type: 'number', label: 'Amount', description: t('indicators.mockData.fields.loanAmount') },
              { name: 'overdue_amount', type: 'number', label: 'Overdue Amount', description: t('indicators.mockData.fields.overdueAmount') },
              { name: 'overdue_days', type: 'number', label: 'Overdue Days', description: t('indicators.mockData.fields.overdueDays') },
              { name: 'customer_id', type: 'text', label: 'Customer ID', description: t('indicators.mockData.fields.customerId') },
              { name: 'status', type: 'text', label: 'Status', description: t('indicators.mockData.fields.loanStatus') },
              { name: 'created_date', type: 'date', label: 'Created Date', description: t('indicators.mockData.fields.loanCreatedDate') },
              { name: 'is_active', type: 'boolean', label: 'Is Active', description: t('indicators.mockData.fields.loanActive') }
            ]
          },
          { 
            id: 'customers', 
            name: 'Customers', 
            fields: [
              { name: 'customer_id', type: 'text', label: 'Customer ID', description: t('indicators.mockData.fields.customerId') },
              { name: 'name', type: 'text', label: 'Name', description: t('indicators.mockData.fields.customerName') },
              { name: 'income', type: 'number', label: 'Income', description: t('indicators.mockData.fields.income') },
              { name: 'risk_score', type: 'number', label: 'Risk Score', description: t('indicators.mockData.fields.riskScore') },
              { name: 'registration_date', type: 'date', label: 'Registration Date', description: t('indicators.mockData.fields.registrationDate') },
              { name: 'is_vip', type: 'boolean', label: 'Is VIP', description: t('indicators.mockData.fields.isVip') }
            ]
          },
          { 
            id: 'payments', 
            name: 'Payments', 
            fields: [
              { name: 'payment_id', type: 'text', label: 'Payment ID', description: t('indicators.mockData.fields.paymentId') },
              { name: 'amount', type: 'number', label: 'Amount', description: t('indicators.mockData.fields.paymentAmount') },
              { name: 'payment_date', type: 'date', label: 'Payment Date', description: t('indicators.mockData.fields.paymentDate') },
              { name: 'late_flag', type: 'boolean', label: 'Late Flag', description: t('indicators.mockData.fields.lateFlag') },
              { name: 'description', type: 'text', label: 'Description', description: t('indicators.mockData.fields.paymentDescription') }
            ]
          }
        ]
      },
      { 
        id: 'mock-2', 
        code: 'CIC_DATA', 
        name: 'Dữ liệu CIC (Mock)',
        description: t('indicators.mockData.cic.description'),
        tables: [
          { 
            id: 'credit_score', 
            name: 'Credit Score', 
            fields: [
              { name: 'customer_id', type: 'text', label: 'Customer ID', description: t('indicators.mockData.fields.customerId') },
              { name: 'score', type: 'number', label: 'Score', description: t('indicators.mockData.fields.creditScore') },
              { name: 'rank', type: 'text', label: 'Rank', description: t('indicators.mockData.fields.creditRank') },
              { name: 'last_update', type: 'date', label: 'Last Update', description: t('indicators.mockData.fields.lastUpdate') },
              { name: 'has_bad_debt', type: 'boolean', label: 'Has Bad Debt', description: t('indicators.mockData.fields.hasBadDebt') }
            ]
          }
        ]
      }
    ];
    
    // Map uploaded files to data source format
    const mappedSources = uploadedFiles.map(ds => {
      const sourceName = ds.sourceName || t('indicators.unknownSource');
      const fileName = ds.name.replace(/\.(csv|xlsx|xls)$/i, ''); // Remove extension
      const rowCount = ds.rowCount || 0;
      const sheetCount = ds.sheetCount || (ds.data?.sheets?.length || 0);
      const uploadDate = ds.uploadDate ? new Date(ds.uploadDate).toLocaleDateString('vi-VN') : 'N/A';
      
      return {
        id: ds.id,
        code: ds.id, // Use id as code
        name: `[${sourceName}] ${fileName}`, // Include source name prefix
        sourceName: sourceName,
        description: `📁 ${ds.type} file • ${sheetCount} sheet(s) • ${rowCount.toLocaleString()} records • Uploaded ${uploadDate}`,
        tables: ds.data?.sheets?.map(sheet => ({
          id: sheet.name.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, ''),
          name: sheet.name,
          fields: (sheet.headers || []).map(header => {
            // Find first non-null value to infer type
            const sampleValue = (sheet.data || []).find(row => row[header] !== null && row[header] !== undefined)?.[header];
            const fieldType = inferFieldType(sampleValue);
            
            return {
              name: header.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, ''),
              type: fieldType,
              label: header,
              description: `${fieldType} field from ${sheet.name} sheet`
            };
          })
        })) || []
      };
    });
    
    return [...mockSources, ...mappedSources];
  }, [dataSources]);

  const [indicators, setIndicators] = useState([
    { 
      id: 'IND-FIN-001', 
      name: 'Tỷ lệ nợ quá hạn', 
      category: 'Tài chính',
      description: 'Tỷ lệ nợ quá hạn trên tổng dư nợ',
      unit: '%', 
      dataType: 'number',
      dataSource: 'CORE_BANKING',
      collectType: 'Tự động',
      targetObject: 'Khách hàng doanh nghiệp',
      hasWeight: true,
      hasDefaultValue: false,
      status: 'active',
      createdDate: '2026-05-15',
      variables: [
        { name: 'overdue_amount', label: 'Dư nợ quá hạn', source: 'CORE_BANKING', table: 'loans', field: 'overdue_amount', fieldType: 'number' },
        { name: 'total_amount', label: 'Tổng dư nợ', source: 'CORE_BANKING', table: 'loans', field: 'amount', fieldType: 'number' }
      ],
      processedVariables: [
        { name: 'value', label: 'Giá trị chỉ tiêu', formula: '(@overdue_amount / @total_amount) * 100' }
      ],
      thresholds: {
        high: { conditions: [{ variable: 'value', operator: '>=', value: '5' }], logic: 'AND' },
        medium: { conditions: [{ variable: 'value', operator: '>=', value: '1' }, { variable: 'value', operator: '<', value: '5' }], logic: 'AND' },
        low: { conditions: [{ variable: 'value', operator: '>', value: '0' }, { variable: 'value', operator: '<', value: '1' }], logic: 'AND' },
        none: { conditions: [{ variable: 'value', operator: '<=', value: '0' }], logic: 'AND' }
      }
    },
    { 
      id: 'IND-PAY-001', 
      name: 'Số lần thanh toán trễ', 
      category: 'Thanh toán',
      description: 'Số lần khách hàng thanh toán trễ hạn trong 6 tháng',
      unit: 'Lần', 
      dataType: 'number',
      dataSource: 'CORE_BANKING',
      collectType: 'Tự động',
      targetObject: 'Tất cả',
      hasWeight: false,
      hasDefaultValue: false,
      status: 'active',
      createdDate: '2026-05-20',
      variables: [
        { name: 'late_count', label: 'Số lần trễ', source: 'CORE_BANKING', table: 'payments', field: 'late_flag', fieldType: 'boolean' }
      ],
      processedVariables: [
        { name: 'value', label: 'Giá trị chỉ tiêu', formula: 'COUNT(@late_count WHERE @late_count == true)' }
      ],
      thresholds: {
        high: { conditions: [{ variable: 'value', operator: '>=', value: '3' }], logic: 'AND' },
        medium: { conditions: [{ variable: 'value', operator: '==', value: '2' }], logic: 'AND' },
        low: { conditions: [{ variable: 'value', operator: '==', value: '1' }], logic: 'AND' },
        none: { conditions: [{ variable: 'value', operator: '==', value: '0' }], logic: 'AND' }
      }
    },
    { 
      id: 'IND-BEH-001', 
      name: 'Điểm tín dụng CIC', 
      category: 'Bên ngoài',
      description: 'Điểm tín dụng từ Trung tâm Thông tin tín dụng',
      unit: 'Điểm', 
      dataType: 'number',
      dataSource: 'CIC_DATA',
      collectType: 'Tự động',
      targetObject: 'Tất cả',
      hasWeight: true,
      hasDefaultValue: false,
      status: 'inactive',
      createdDate: '2026-05-10',
      variables: [
        { name: 'cic_score', label: 'Điểm CIC', source: 'CIC_DATA', table: 'credit_score', field: 'score', fieldType: 'number' }
      ],
      processedVariables: [
        { name: 'value', label: 'Giá trị chỉ tiêu', formula: '@cic_score' }
      ],
      thresholds: {
        high: { conditions: [{ variable: 'value', operator: '<', value: '500' }], logic: 'AND' },
        medium: { conditions: [{ variable: 'value', operator: '>=', value: '500' }, { variable: 'value', operator: '<', value: '700' }], logic: 'AND' },
        low: { conditions: [{ variable: 'value', operator: '>=', value: '700' }, { variable: 'value', operator: '<', value: '850' }], logic: 'AND' },
        none: { conditions: [{ variable: 'value', operator: '>=', value: '850' }], logic: 'AND' }
      }
    }
  ]);

  const [formData, setFormData] = useState({
    name: '',
    category: 'Tài chính',
    description: '',
    unit: '',
    dataType: 'number',
    dataSource: '',
    collectType: 'Tự động',
    targetObject: 'Khách hàng doanh nghiệp',
    hasWeight: false,
    hasDefaultValue: false,
    defaultValue: '',
    validExample: '',
    notes: '',
    variables: [],
    processedVariables: [{ name: 'value', label: 'Giá trị chỉ tiêu', formula: '' }],
    thresholds: {
      high: { conditions: [{ variable: '', operator: '>=', value: '' }], logic: 'AND' },
      medium: { conditions: [{ variable: '', operator: '>=', value: '' }], logic: 'AND' },
      low: { conditions: [{ variable: '', operator: '>', value: '' }], logic: 'AND' },
      none: { conditions: [{ variable: '', operator: '<=', value: '' }], logic: 'AND' }
    }
  });

  // Add data source variable
  const [selectedSource, setSelectedSource] = useState(null);
  const [selectedTable, setSelectedTable] = useState(null);
  const [selectedField, setSelectedField] = useState(null);
  const [customVarName, setCustomVarName] = useState('');

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleDataSourceChange = (selectedOption) => {
    setSelectedSource(selectedOption);
    setSelectedTable(null);
    setSelectedField(null);
    setFormData(prev => ({ ...prev, dataSource: selectedOption ? selectedOption.value : '' }));
  };

  const handleAddVariable = () => {
    if (!selectedSource || !selectedTable || !selectedField) {
      showToast(t('indicators.messages.selectAllFields'), 'warning');
      return;
    }

    // Use custom name if provided, otherwise use default var_X
    const defaultName = `var_${formData.variables.length + 1}`;
    const varName = customVarName.trim() || defaultName;

    // Check if variable name already exists
    if (formData.variables.some(v => v.name === varName)) {
      showToast(t('indicators.messages.variableExists', { name: varName }), 'warning');
      return;
    }

    // Get field type from data source structure
    const fieldInfo = availableDataSources
      .find(ds => ds.code === selectedSource.value)
      ?.tables.find(t => t.id === selectedTable.value)
      ?.fields.find(f => f.name === selectedField.value);

    const newVariable = {
      name: varName,
      label: selectedField.label.replace(/ \([^)]+\)$/, ''), // Remove (type) from label
      source: selectedSource.value,
      table: selectedTable.value,
      field: selectedField.value,
      fieldType: fieldInfo?.type || 'text',
      fieldDescription: fieldInfo?.description || ''
    };

    setFormData(prev => ({
      ...prev,
      variables: [...prev.variables, newVariable]
    }));

    // Auto update default formula for 'value' if it's the first variable
    if (formData.variables.length === 0 && formData.processedVariables[0].formula === '') {
      setFormData(prev => ({
        ...prev,
        processedVariables: [{ name: 'value', label: 'Giá trị chỉ tiêu', formula: `@${varName}` }]
      }));
    }

    showToast(t('indicators.messages.variableAdded', { name: varName }), 'success');
    setSelectedField(null);
    setCustomVarName('');
  };

  const handleRemoveVariable = (index) => {
    setFormData(prev => ({
      ...prev,
      variables: prev.variables.filter((_, i) => i !== index)
    }));
  };

  const handleAddProcessedVariable = () => {
    setFormData(prev => ({
      ...prev,
      processedVariables: [
        ...prev.processedVariables,
        { name: `calc_${prev.processedVariables.length}`, label: '', formula: '' }
      ]
    }));
  };

  const handleProcessedVariableChange = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      processedVariables: prev.processedVariables.map((v, i) =>
        i === index ? { ...v, [field]: value } : v
      )
    }));
  };

  const handleRemoveProcessedVariable = (index) => {
    if (index === 0) {
      showToast(t('indicators.messages.cannotDeleteValue'), 'warning');
      return;
    }
    setFormData(prev => ({
      ...prev,
      processedVariables: prev.processedVariables.filter((_, i) => i !== index)
    }));
  };

  const handleAddThresholdCondition = (level) => {
    setFormData(prev => ({
      ...prev,
      thresholds: {
        ...prev.thresholds,
        [level]: {
          ...prev.thresholds[level],
          conditions: [
            ...prev.thresholds[level].conditions,
            { variable: '', operator: '>=', value: '' }
          ]
        }
      }
    }));
  };

  const handleThresholdConditionChange = (level, index, field, value) => {
    setFormData(prev => ({
      ...prev,
      thresholds: {
        ...prev.thresholds,
        [level]: {
          ...prev.thresholds[level],
          conditions: prev.thresholds[level].conditions.map((c, i) =>
            i === index ? { ...c, [field]: value } : c
          )
        }
      }
    }));
  };

  const handleRemoveThresholdCondition = (level, index) => {
    if (formData.thresholds[level].conditions.length === 1) {
      showToast(t('indicators.messages.minOneCondition'), 'warning');
      return;
    }
    setFormData(prev => ({
      ...prev,
      thresholds: {
        ...prev.thresholds,
        [level]: {
          ...prev.thresholds[level],
          conditions: prev.thresholds[level].conditions.filter((_, i) => i !== index)
        }
      }
    }));
  };

  const handleThresholdLogicChange = (level, logic) => {
    setFormData(prev => ({
      ...prev,
      thresholds: {
        ...prev.thresholds,
        [level]: {
          ...prev.thresholds[level],
          logic
        }
      }
    }));
  };

  const openCreateModal = () => {
    setEditingIndicator(null);
    setFormData({
      name: '',
      category: 'Tài chính',
      description: '',
      unit: '',
      dataType: 'number',
      dataSource: '',
      collectType: 'Tự động',
      targetObject: 'Khách hàng doanh nghiệp',
      hasWeight: false,
      hasDefaultValue: false,
      defaultValue: '',
      validExample: '',
      notes: '',
      variables: [],
      processedVariables: [{ name: 'value', label: 'Giá trị chỉ tiêu', formula: '' }],
      thresholds: {
        high: { conditions: [{ variable: '', operator: '>=', value: '' }], logic: 'AND' },
        medium: { conditions: [{ variable: '', operator: '>=', value: '' }], logic: 'AND' },
        low: { conditions: [{ variable: '', operator: '>', value: '' }], logic: 'AND' },
        none: { conditions: [{ variable: '', operator: '<=', value: '' }], logic: 'AND' }
      }
    });
    setSelectedSource(null);
    setSelectedTable(null);
    setSelectedField(null);
    setCustomVarName('');
    setShowModal(true);
  };

  const openEditModal = (indicator) => {
    setEditingIndicator(indicator);
    setFormData({
      name: indicator.name || '',
      category: indicator.category || 'Tài chính',
      description: indicator.description || '',
      unit: indicator.unit || '',
      dataType: indicator.dataType || 'number',
      dataSource: indicator.dataSource || '',
      collectType: indicator.collectType || 'Tự động',
      targetObject: indicator.targetObject || 'Khách hàng doanh nghiệp',
      hasWeight: indicator.hasWeight || false,
      hasDefaultValue: indicator.hasDefaultValue || false,
      defaultValue: indicator.defaultValue || '',
      validExample: indicator.validExample || '',
      notes: indicator.notes || '',
      variables: indicator.variables || [],
      processedVariables: indicator.processedVariables || [{ name: 'value', label: 'Giá trị chỉ tiêu', formula: '' }],
      thresholds: indicator.thresholds || {
        high: { conditions: [{ variable: '', operator: '>=', value: '' }], logic: 'AND' },
        medium: { conditions: [{ variable: '', operator: '>=', value: '' }], logic: 'AND' },
        low: { conditions: [{ variable: '', operator: '>', value: '' }], logic: 'AND' },
        none: { conditions: [{ variable: '', operator: '<=', value: '' }], logic: 'AND' }
      }
    });
    setShowModal(true);
  };

  const handleSave = (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.unit) {
      showToast(t('indicators.messages.fillRequired'), 'warning');
      return;
    }

    if (editingIndicator) {
      setIndicators(indicators.map(ind => 
        ind.id === editingIndicator.id 
          ? { ...ind, ...formData }
          : ind
      ));
      showToast(t('indicators.messages.updateSuccess'), 'success');
    } else {
      const categoryPrefix = {
        'Tài chính': 'FIN',
        'Thanh toán': 'PAY',
        'Hành vi': 'BEH',
        'Bên ngoài': 'EXT',
        'Khác': 'OTH'
      };
      const prefix = categoryPrefix[formData.category] || 'OTH';
      const newId = `IND-${prefix}-${String(indicators.filter(i => i.category === formData.category).length + 1).padStart(3, '0')}`;
      
      const newIndicator = {
        id: newId,
        ...formData,
        status: 'active',
        createdDate: new Date().toISOString().split('T')[0]
      };
      setIndicators([...indicators, newIndicator]);
      showToast(t('indicators.messages.createSuccess'), 'success');
    }
    setShowModal(false);
  };

  const deleteIndicator = (id) => {
    if (window.confirm(t('indicators.messages.deleteConfirm'))) {
      setIndicators(indicators.filter(ind => ind.id !== id));
      showToast(t('indicators.messages.deleteSuccess'), 'success');
    }
  };

  const toggleIndicatorStatus = (id) => {
    setIndicators(indicators.map(ind => 
      ind.id === id 
        ? { ...ind, status: ind.status === 'active' ? 'inactive' : 'active' }
        : ind
    ));
    const indicator = indicators.find(ind => ind.id === id);
    showToast(t('indicators.messages.toggleSuccess', { action: indicator.status === 'active' ? t('indicators.deactivate') : t('indicators.activate') }), 'success');
  };

  const cloneIndicator = (indicator) => {
    const categoryPrefix = {
      'Tài chính': 'FIN',
      'Thanh toán': 'PAY',
      'Hành vi': 'BEH',
      'Bên ngoài': 'EXT',
      'Khác': 'OTH'
    };
    const prefix = categoryPrefix[indicator.category] || 'OTH';
    const newId = `IND-${prefix}-${String(indicators.filter(i => i.category === indicator.category).length + 1).padStart(3, '0')}`;
    
    const clonedIndicator = {
      ...indicator,
      id: newId,
      name: `${indicator.name} (Sao chép)`,
      status: 'inactive',
      createdDate: new Date().toISOString().split('T')[0]
    };
    
    setIndicators([...indicators, clonedIndicator]);
    showToast(t('indicators.messages.cloneSuccess'), 'success');
  };

  const openQuickView = (indicator) => {
    setQuickViewIndicator(indicator);
    setShowQuickViewModal(true);
  };

  const categories = ['Tất cả', 'Tài chính', 'Thanh toán', 'Hành vi', 'Bên ngoài', 'Khác'];
  
  // Advanced filtering
  const filteredIndicators = indicators.filter(ind => {
    // Search filter
    const matchesSearch = !searchTerm || 
      ind.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      ind.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (ind.description && ind.description.toLowerCase().includes(searchTerm.toLowerCase()));
    
    // Category filter
    const matchesCategory = selectedCategories.length === 0 || 
      selectedCategories.includes(ind.category);
    
    // Collect type filter
    const matchesCollectType = selectedCollectTypes.length === 0 || 
      selectedCollectTypes.includes(ind.collectType);
    
    // Status filter
    const matchesStatus = selectedStatuses.length === 0 || 
      selectedStatuses.includes(ind.status);
    
    // Tab filter
    const matchesTab = activeTab === 'all' || activeTab === 'Tất cả' || ind.category === activeTab;
    
    return matchesSearch && matchesCategory && matchesCollectType && matchesStatus && matchesTab;
  });

  // Calculate statistics
  const stats = {
    total: indicators.length,
    active: indicators.filter(i => i.status === 'active').length,
    inactive: indicators.filter(i => i.status === 'inactive').length,
    auto: indicators.filter(i => i.collectType === 'Tự động').length,
    manual: indicators.filter(i => i.collectType === 'Thủ công').length,
    avgVariables: indicators.length > 0 ? (indicators.reduce((sum, i) => sum + (i.variables?.length || 0), 0) / indicators.length).toFixed(1) : 0,
    highRisk: indicators.filter(i => i.status === 'active' && i.thresholds?.high).length
  };

  // Get category color
  const getCategoryColor = (category) => {
    const colors = {
      'Tài chính': 'success',
      'Thanh toán': 'primary',
      'Hành vi': 'warning',
      'Bên ngoài': 'info',
      'Khác': 'secondary'
    };
    return colors[category] || 'secondary';
  };

  // Get category icon
  const getCategoryIcon = (category) => {
    const icons = {
      'Tài chính': 'cash-coin',
      'Thanh toán': 'credit-card',
      'Hành vi': 'graph-up',
      'Bên ngoài': 'globe',
      'Khác': 'three-dots'
    };
    return icons[category] || 'three-dots';
  };

  // Options for react-select
  const dataSourceOptions = availableDataSources.map(ds => ({
    value: ds.code,
    label: `${ds.code} - ${ds.name}`,
    description: ds.description
  }));

  const tableOptions = selectedSource
    ? availableDataSources
        .find(ds => ds.code === selectedSource.value)
        ?.tables.map(t => ({ value: t.id, label: t.name })) || []
    : [];

  const fieldOptions = selectedTable
    ? availableDataSources
        .find(ds => ds.code === selectedSource?.value)
        ?.tables.find(t => t.id === selectedTable.value)
        ?.fields.map(f => ({ 
            value: f.name, 
            label: `${f.label} (${f.type})`,
            type: f.type,
            description: f.description
          })) || []
    : [];

  const getAllVariableOptions = () => {
    const sourceVars = formData.variables.map(v => ({ value: v.name, label: `@${v.name} (${v.label})`, fieldType: v.fieldType }));
    const processedVars = formData.processedVariables.map(v => ({ value: v.name, label: `@${v.name} (${v.label})`, fieldType: 'number' }));
    return [...sourceVars, ...processedVars];
  };

  // Get field type for a variable
  const getVariableFieldType = (varName) => {
    const sourceVar = formData.variables.find(v => v.name === varName);
    if (sourceVar) return sourceVar.fieldType || 'text';
    // Processed variables are always number
    return 'number';
  };

  // Get operators based on field type
  const getOperatorsForType = (fieldType) => {
    switch (fieldType) {
      case 'number':
        return [
          { value: '>=', label: '>=' },
          { value: '>', label: '>' },
          { value: '<=', label: '<=' },
          { value: '<', label: '<' },
          { value: '==', label: '=' },
          { value: '!=', label: '!=' }
        ];
      case 'text':
        return [
          { value: '==', label: 'Bằng' },
          { value: '!=', label: 'Khác' },
          { value: 'contains', label: 'Chứa' },
          { value: 'startsWith', label: 'Bắt đầu với' },
          { value: 'endsWith', label: 'Kết thúc với' }
        ];
      case 'date':
        return [
          { value: '>=', label: 'Sau hoặc bằng' },
          { value: '>', label: 'Sau' },
          { value: '<=', label: 'Trước hoặc bằng' },
          { value: '<', label: 'Trước' },
          { value: '==', label: 'Bằng' },
          { value: '!=', label: 'Khác' }
        ];
      case 'boolean':
        return [
          { value: '==', label: 'Bằng' },
          { value: '!=', label: 'Khác' }
        ];
      default:
        return [
          { value: '==', label: '=' },
          { value: '!=', label: '!=' }
        ];
    }
  };

  return (
    <div className="container-fluid">
      {/* Header */}
      <div className="page-header-sticky">
        <div className="d-flex justify-content-between align-items-center">
          <div>
            <h2 className="mb-1">
              <i className="bi bi-bar-chart me-2"></i>
              {t('indicators.title')}
            </h2>
            <p className="text-muted mb-0">{t('indicators.subtitle')}</p>
          </div>
          <div>
            <button className="btn btn-primary" onClick={openCreateModal}>
              <i className="bi bi-plus-circle me-2"></i>
              {t('indicators.createIndicator')}
            </button>
          </div>
        </div>
      </div>

      {/* Enhanced Statistics */}
      <div className="row mb-4 stagger-animation">
        <div className="col-xl-2 col-md-4 col-sm-6 mb-3">
          <div className="card border-0 shadow-sm h-100 hover-lift" style={{ borderLeft: '4px solid #6c757d' }}>
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-start">
                <div>
                  <h6 className="text-muted mb-1" style={{ fontSize: '0.85rem' }}>{t('indicators.totalIndicators')}</h6>
                  <h3 className="mb-0 fw-bold">{stats.total}</h3>
                </div>
                <div className="bg-secondary bg-opacity-10 rounded p-2">
                  <i className="bi bi-bar-chart-fill text-secondary" style={{ fontSize: '1.5rem' }}></i>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="col-xl-2 col-md-4 col-sm-6 mb-3">
          <div className="card border-0 shadow-sm h-100 hover-lift" style={{ borderLeft: '4px solid #28a745' }}>
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-start">
                <div>
                  <h6 className="text-muted mb-1" style={{ fontSize: '0.85rem' }}>{t('indicators.activeIndicators')}</h6>
                  <h3 className="mb-0 fw-bold text-success">{stats.active}</h3>
                </div>
                <div className="bg-success bg-opacity-10 rounded p-2">
                  <i className="bi bi-check-circle-fill text-success" style={{ fontSize: '1.5rem' }}></i>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="col-xl-2 col-md-4 col-sm-6 mb-3">
          <div className="card border-0 shadow-sm h-100" style={{ borderLeft: '4px solid #dc3545' }}>
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-start">
                <div>
                  <h6 className="text-muted mb-1" style={{ fontSize: '0.85rem' }}>{t('indicators.inactiveIndicators')}</h6>
                  <h3 className="mb-0 fw-bold text-danger">{stats.inactive}</h3>
                </div>
                <div className="bg-danger bg-opacity-10 rounded p-2">
                  <i className="bi bi-x-circle-fill text-danger" style={{ fontSize: '1.5rem' }}></i>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="col-xl-2 col-md-4 col-sm-6 mb-3">
          <div className="card border-0 shadow-sm h-100 hover-lift" style={{ borderLeft: '4px solid #17a2b8' }}>
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-start">
                <div>
                  <h6 className="text-muted mb-1" style={{ fontSize: '0.85rem' }}>{t('indicators.automaticCollection')}</h6>
                  <h3 className="mb-0 fw-bold text-info">{stats.auto}</h3>
                </div>
                <div className="bg-info bg-opacity-10 rounded p-2">
                  <i className="bi bi-robot text-info" style={{ fontSize: '1.5rem' }}></i>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="col-xl-2 col-md-4 col-sm-6 mb-3">
          <div className="card border-0 shadow-sm h-100 hover-lift" style={{ borderLeft: '4px solid #ffc107' }}>
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-start">
                <div>
                  <h6 className="text-muted mb-1" style={{ fontSize: '0.85rem' }}>{t('indicators.avgComplexity')}</h6>
                  <h3 className="mb-0 fw-bold text-warning">{stats.avgVariables}</h3>
                  <small className="text-muted">{t('indicators.variablesPerIndicator')}</small>
                </div>
                <div className="bg-warning bg-opacity-10 rounded p-2">
                  <i className="bi bi-diagram-3-fill text-warning" style={{ fontSize: '1.5rem' }}></i>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="col-xl-2 col-md-4 col-sm-6 mb-3">
          <div className="card border-0 shadow-sm h-100" style={{ borderLeft: '4px solid #dc3545' }}>
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-start">
                <div>
                  <h6 className="text-muted mb-1" style={{ fontSize: '0.85rem' }}>{t('indicators.highRisk')}</h6>
                  <h3 className="mb-0 fw-bold text-danger">{stats.highRisk}</h3>
                  <small className="text-muted">{t('indicators.withThresholds')}</small>
                </div>
                <div className="bg-danger bg-opacity-10 rounded p-2">
                  <i className="bi bi-exclamation-triangle-fill text-danger" style={{ fontSize: '1.5rem' }}></i>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="card border-0 shadow-sm mb-4">
        <div className="card-body">
          <div className="row g-3">
            {/* Global Search */}
            <div className="col-lg-4">
              <div className="input-group">
                <span className="input-group-text bg-white">
                  <i className="bi bi-search"></i>
                </span>
                <input
                  type="text"
                  className="form-control"
                  placeholder={t('indicators.searchPlaceholder')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                {searchTerm && (
                  <button 
                    className="btn btn-outline-secondary" 
                    onClick={() => setSearchTerm('')}
                  >
                    <i className="bi bi-x"></i>
                  </button>
                )}
              </div>
            </div>

            {/* Category Filter */}
            <div className="col-lg-2">
              <Select
                isMulti
                options={categories.filter(c => c !== 'Tất cả').map(c => ({ value: c, label: c }))}
                value={selectedCategories.map(c => ({ value: c, label: c }))}
                onChange={(selected) => setSelectedCategories(selected ? selected.map(s => s.value) : [])}
                placeholder={t('indicators.categoryPlaceholder')}
                isClearable
                styles={{
                  control: (base) => ({ ...base, minHeight: '38px' }),
                  multiValue: (base) => ({ ...base, backgroundColor: '#e7f3ff' })
                }}
              />
            </div>

            {/* Collect Type Filter */}
            <div className="col-lg-2">
              <Select
                isMulti
                options={[
                  { value: 'Tự động', label: `🤖 ${t('indicators.collectTypes.automatic')}` },
                  { value: 'Thủ công', label: `👤 ${t('indicators.collectTypes.manual')}` }
                ]}
                value={selectedCollectTypes.map(t => ({ value: t, label: t }))}
                onChange={(selected) => setSelectedCollectTypes(selected ? selected.map(s => s.value) : [])}
                placeholder={t('indicators.collectTypePlaceholder')}
                isClearable
                styles={{
                  control: (base) => ({ ...base, minHeight: '38px' }),
                  multiValue: (base) => ({ ...base, backgroundColor: '#e7f3ff' })
                }}
              />
            </div>

            {/* Status Filter */}
            <div className="col-lg-2">
              <Select
                isMulti
                options={[
                  { value: 'active', label: `✅ ${t('status.active')}` },
                  { value: 'inactive', label: `❌ ${t('status.inactive')}` }
                ]}
                value={selectedStatuses.map(s => ({ value: s, label: s === 'active' ? `✅ ${t('status.active')}` : `❌ ${t('status.inactive')}` }))}
                onChange={(selected) => setSelectedStatuses(selected ? selected.map(s => s.value) : [])}
                placeholder={t('common.status')}
                isClearable
                styles={{
                  control: (base) => ({ ...base, minHeight: '38px' }),
                  multiValue: (base) => ({ ...base, backgroundColor: '#e7f3ff' })
                }}
              />
            </div>

            {/* View Mode Toggle */}
            <div className="col-lg-2">
              <div className="btn-group w-100" role="group">
                <button
                  className={`btn ${viewMode === 'grid' ? 'btn-primary' : 'btn-outline-secondary'}`}
                  onClick={() => setViewMode('grid')}
                  title={t('indicators.viewModes.grid')}
                >
                  <i className="bi bi-grid-3x3-gap"></i>
                </button>
                <button
                  className={`btn ${viewMode === 'list' ? 'btn-primary' : 'btn-outline-secondary'}`}
                  onClick={() => setViewMode('list')}
                  title={t('indicators.viewModes.list')}
                >
                  <i className="bi bi-list-ul"></i>
                </button>
                <button
                  className={`btn ${viewMode === 'table' ? 'btn-primary' : 'btn-outline-secondary'}`}
                  onClick={() => setViewMode('table')}
                  title={t('indicators.viewModes.table')}
                >
                  <i className="bi bi-table"></i>
                </button>
              </div>
            </div>
          </div>

          {/* Active Filters Display */}
          {(searchTerm || selectedCategories.length > 0 || selectedCollectTypes.length > 0) && (
            <div className="mt-3 d-flex align-items-center flex-wrap gap-2">
              <small className="text-muted">{t('indicators.filtering')}:</small>
              {searchTerm && (
                <span className="badge bg-primary">
                  {t('common.search')}: "{searchTerm}"
                  <i className="bi bi-x ms-1 cursor-pointer" onClick={() => setSearchTerm('')}></i>
                </span>
              )}
              {selectedCategories.map(cat => (
                <span key={cat} className="badge bg-success">
                  {cat}
                  <i className="bi bi-x ms-1 cursor-pointer" onClick={() => setSelectedCategories(selectedCategories.filter(c => c !== cat))}></i>
                </span>
              ))}
              {selectedCollectTypes.map(type => (
                <span key={type} className="badge bg-info">
                  {type}
                  <i className="bi bi-x ms-1 cursor-pointer" onClick={() => setSelectedCollectTypes(selectedCollectTypes.filter(t => t !== type))}></i>
                </span>
              ))}
              <button
                className="btn btn-sm btn-outline-secondary"
                onClick={() => {
                  setSearchTerm('');
                  setSelectedCategories([]);
                  setSelectedCollectTypes([]);
                }}
              >
                {t('common.clearAll')}
              </button>
            </div>
          )}

          {/* Results count */}
          <div className="mt-3">
            <small className="text-muted">
              {t('indicators.showing')} <strong>{filteredIndicators.length}</strong> / {indicators.length} {t('indicators.indicators')}
            </small>
          </div>
        </div>
      </div>

      {/* Indicators Display */}
      {viewMode === 'grid' && (
        <div className="row g-4">
          {filteredIndicators.length > 0 ? (
            filteredIndicators.map(indicator => (
              <div key={indicator.id} className="col-lg-4 col-md-6">
                <div className="card border-0 shadow-sm h-100" style={{ borderLeft: `4px solid var(--bs-${getCategoryColor(indicator.category)})` }}>
                  <div className="card-body">
                    {/* Header */}
                    <div className="d-flex justify-content-between align-items-start mb-3">
                      <div>
                        <span className={`badge bg-${getCategoryColor(indicator.category)} mb-2`}>
                          <i className={`bi bi-${getCategoryIcon(indicator.category)} me-1`}></i>
                          {indicator.category}
                        </span>
                        <div className="small text-muted">{indicator.id}</div>
                      </div>
                      <span className={`badge bg-${indicator.status === 'active' ? 'success' : 'secondary'}`}>
                        {indicator.status === 'active' ? '✅ Active' : '⏸️ Inactive'}
                      </span>
                    </div>

                    {/* Title & Description */}
                    <h5 className="card-title mb-2">{indicator.name}</h5>
                    <p className="text-muted small mb-3" style={{ minHeight: '40px' }}>
                      {indicator.description || t('indicators.noDescription')}
                    </p>

                    {/* Complexity Info */}
                    <div className="mb-3 d-flex flex-wrap gap-2">
                      <span className="badge bg-light text-dark border">
                        <i className="bi bi-collection me-1"></i>
                        {indicator.variables?.length || 0} {t('indicators.variables')}
                      </span>
                      <span className="badge bg-light text-dark border">
                        <i className="bi bi-calculator me-1"></i>
                        {indicator.processedVariables?.length || 0} {t('indicators.formulas')}
                      </span>
                      <span className="badge bg-light text-dark border">
                        <i className="bi bi-exclamation-triangle me-1"></i>
                        4 {t('indicators.thresholds')}
                      </span>
                    </div>

                    {/* Meta Info */}
                    <div className="mb-3">
                      <div className="d-flex align-items-center mb-2">
                        <i className="bi bi-bullseye text-muted me-2"></i>
                        <small className="text-muted">{indicator.targetObject}</small>
                      </div>
                      <div className="d-flex align-items-center mb-2">
                        <i className="bi bi-database text-muted me-2"></i>
                        <small className="text-muted">{indicator.dataSource}</small>
                      </div>
                      <div className="d-flex align-items-center">
                        <i className={`bi bi-${indicator.collectType === 'Tự động' ? 'robot' : 'person'} text-muted me-2`}></i>
                        <span className={`badge bg-${indicator.collectType === 'Tự động' ? 'info' : 'warning'}`}>
                          {indicator.collectType}
                        </span>
                        <span className="badge bg-light text-dark ms-2">{indicator.unit}</span>
                      </div>
                    </div>

                    {/* Threshold Preview */}
                    <div className="mb-3">
                      <div className="d-flex gap-1">
                        <div className="flex-fill" style={{ height: '6px', background: '#dc3545', borderRadius: '3px' }} title="Rủi ro cao"></div>
                        <div className="flex-fill" style={{ height: '6px', background: '#ffc107', borderRadius: '3px' }} title="Rủi ro trung bình"></div>
                        <div className="flex-fill" style={{ height: '6px', background: '#17a2b8', borderRadius: '3px' }} title="Rủi ro thấp"></div>
                        <div className="flex-fill" style={{ height: '6px', background: '#28a745', borderRadius: '3px' }} title={t('indicators.noRisk')}></div>
                      </div>
                      <div className="d-flex justify-content-between mt-1">
                        <small className="text-danger">{t('indicators.severityLevels.high')}</small>
                        <small className="text-warning">{t('indicators.severityLevels.medium')}</small>
                        <small className="text-info">{t('indicators.severityLevels.low')}</small>
                        <small className="text-success">OK</small>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="d-flex gap-2">
                      <button
                        className="btn btn-sm btn-outline-primary flex-fill"
                        onClick={() => openQuickView(indicator)}
                        title={t('indicators.quickView')}
                      >
                        <i className="bi bi-eye"></i>
                      </button>
                      <button
                        className="btn btn-sm btn-outline-secondary flex-fill"
                        onClick={() => openEditModal(indicator)}
                        title={t('common.edit')}
                      >
                        <i className="bi bi-pencil"></i>
                      </button>
                      <button
                        className="btn btn-sm btn-outline-info flex-fill"
                        onClick={() => cloneIndicator(indicator)}
                        title={t('indicators.clone')}
                      >
                        <i className="bi bi-files"></i>
                      </button>
                      <button
                        className={`btn btn-sm btn-outline-${indicator.status === 'active' ? 'warning' : 'success'} flex-fill`}
                        onClick={() => toggleIndicatorStatus(indicator.id)}
                        title={indicator.status === 'active' ? t('indicators.deactivate') : t('indicators.activate')}
                      >
                        <i className={`bi bi-${indicator.status === 'active' ? 'pause-circle' : 'play-circle'}`}></i>
                      </button>
                      <button
                        className="btn btn-sm btn-outline-danger flex-fill"
                        onClick={() => deleteIndicator(indicator.id)}
                        title={t('common.delete')}
                      >
                        <i className="bi bi-trash"></i>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="col-12">
              <div className="card border-0 shadow-sm">
                <div className="card-body text-center py-5">
                  <i className="bi bi-inbox" style={{ fontSize: '48px', opacity: 0.3 }}></i>
                  <p className="text-muted mt-3 mb-0">{t('indicators.noMatchingIndicators')}</p>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {viewMode === 'list' && (
        <div className="card border-0 shadow-sm">
          <div className="list-group list-group-flush">
            {filteredIndicators.length > 0 ? (
              filteredIndicators.map(indicator => (
                <div key={indicator.id} className="list-group-item">
                  <div className="row align-items-center">
                    <div className="col-md-6">
                      <div className="d-flex align-items-start">
                        <div className="flex-shrink-0 me-3">
                          <span className={`badge bg-${getCategoryColor(indicator.category)}`} style={{ fontSize: '1.2rem', padding: '0.5rem' }}>
                            <i className={`bi bi-${getCategoryIcon(indicator.category)}`}></i>
                          </span>
                        </div>
                        <div className="flex-grow-1">
                          <div className="d-flex align-items-center mb-1">
                            <h6 className="mb-0 me-2">{indicator.name}</h6>
                            <span className={`badge bg-${indicator.status === 'active' ? 'success' : 'secondary'} me-2`}>
                              {indicator.status === 'active' ? 'Active' : 'Inactive'}
                            </span>
                            <span className="badge bg-light text-dark">{indicator.id}</span>
                          </div>
                          <p className="text-muted small mb-2">{indicator.description || t('indicators.noDescription')}</p>
                          <div className="d-flex flex-wrap gap-2">
                            <small className="text-muted">
                              <i className="bi bi-collection me-1"></i>{indicator.variables?.length || 0} {t('indicators.variables')}
                            </small>
                            <small className="text-muted">
                              <i className="bi bi-database me-1"></i>{indicator.dataSource}
                            </small>
                            <small className="text-muted">
                              <i className="bi bi-bullseye me-1"></i>{indicator.targetObject}
                            </small>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="d-flex justify-content-end gap-2">
                        <button className="btn btn-sm btn-outline-primary" onClick={() => openQuickView(indicator)}>
                          <i className="bi bi-eye me-1"></i>{t('indicators.view')}
                        </button>
                        <button className="btn btn-sm btn-outline-secondary" onClick={() => openEditModal(indicator)}>
                          <i className="bi bi-pencil me-1"></i>{t('common.edit')}
                        </button>
                        <button className="btn btn-sm btn-outline-info" onClick={() => cloneIndicator(indicator)}>
                          <i className="bi bi-files me-1"></i>{t('indicators.clone')}
                        </button>
                        <button 
                          className={`btn btn-sm btn-outline-${indicator.status === 'active' ? 'warning' : 'success'}`}
                          onClick={() => toggleIndicatorStatus(indicator.id)}
                        >
                          <i className={`bi bi-${indicator.status === 'active' ? 'pause-circle' : 'play-circle'}`}></i>
                        </button>
                        <button className="btn btn-sm btn-outline-danger" onClick={() => deleteIndicator(indicator.id)}>
                          <i className="bi bi-trash"></i>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="list-group-item text-center py-5">
                <i className="bi bi-inbox" style={{ fontSize: '48px', opacity: 0.3 }}></i>
                <p className="text-muted mt-3 mb-0">{t('indicators.noMatchingIndicators')}</p>
              </div>
            )}
          </div>
        </div>
      )}

      {viewMode === 'table' && (
        <div className="card border-0 shadow-sm">
          <div className="card-body">
            <div className="table-responsive">
              <table className="table table-hover">
                <thead className="table-light">
                  <tr>
                    <th>Mã</th>
                    <th>Tên chỉ tiêu</th>
                    <th>Danh mục</th>
                    <th>Trạng thái</th>
                    <th>Đơn vị</th>
                    <th>Nguồn</th>
                    <th>Loại</th>
                    <th>Độ phức tạp</th>
                    <th className="text-end">Thao tác</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredIndicators.length > 0 ? (
                    filteredIndicators.map(indicator => (
                      <tr key={indicator.id}>
                        <td><span className="badge bg-secondary">{indicator.id}</span></td>
                        <td>
                          <strong>{indicator.name}</strong>
                          {indicator.description && (
                            <div className="small text-muted" style={{ maxWidth: '200px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {indicator.description}
                            </div>
                          )}
                        </td>
                        <td>
                          <span className={`badge bg-${getCategoryColor(indicator.category)}`}>
                            {indicator.category}
                          </span>
                        </td>
                        <td>
                          <span className={`badge bg-${indicator.status === 'active' ? 'success' : 'secondary'}`}>
                            {indicator.status === 'active' ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td>{indicator.unit}</td>
                        <td><small className="text-muted">{indicator.dataSource}</small></td>
                        <td>
                          <span className={`badge bg-${indicator.collectType === 'Tự động' ? 'info' : 'warning'}`}>
                            {indicator.collectType}
                          </span>
                        </td>
                        <td>
                          <small className="text-muted">
                            {indicator.variables?.length || 0}v • {indicator.processedVariables?.length || 0}f
                          </small>
                        </td>
                        <td className="text-end">
                          <div className="btn-group btn-group-sm">
                            <button className="btn btn-outline-primary" onClick={() => openQuickView(indicator)} title={t('indicators.view')}>
                              <i className="bi bi-eye"></i>
                            </button>
                            <button className="btn btn-outline-secondary" onClick={() => openEditModal(indicator)} title={t('common.edit')}>
                              <i className="bi bi-pencil"></i>
                            </button>
                            <button className="btn btn-outline-info" onClick={() => cloneIndicator(indicator)} title={t('indicators.clone')}>
                              <i className="bi bi-files"></i>
                            </button>
                            <button 
                              className={`btn btn-outline-${indicator.status === 'active' ? 'warning' : 'success'}`}
                              onClick={() => toggleIndicatorStatus(indicator.id)}
                              title={indicator.status === 'active' ? t('indicators.deactivate') : t('indicators.activate')}
                            >
                              <i className={`bi bi-${indicator.status === 'active' ? 'pause-circle' : 'play-circle'}`}></i>
                            </button>
                            <button className="btn btn-outline-danger" onClick={() => deleteIndicator(indicator.id)} title={t('common.delete')}>
                              <i className="bi bi-trash"></i>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="9" className="text-center text-muted py-4">
                        {t('indicators.noMatchingIndicators')}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Quick View Modal */}
      {showQuickViewModal && quickViewIndicator && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)', overflowY: 'auto' }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <i className="bi bi-eye me-2"></i>
                  {t('indicators.indicatorDetail', { name: quickViewIndicator.name })}
                </h5>
                <button type="button" className="btn-close" onClick={() => setShowQuickViewModal(false)}></button>
              </div>
              <div className="modal-body">
                {/* Basic Info */}
                <div className="row g-3 mb-4">
                  <div className="col-md-6">
                    <label className="text-muted small">{t('indicators.indicatorCode')}</label>
                    <div><strong>{quickViewIndicator.id}</strong></div>
                  </div>
                  <div className="col-md-6">
                    <label className="text-muted small">{t('indicators.category')}</label>
                    <div>
                      <span className={`badge bg-${getCategoryColor(quickViewIndicator.category)}`}>
                        <i className={`bi bi-${getCategoryIcon(quickViewIndicator.category)} me-1`}></i>
                        {quickViewIndicator.category}
                      </span>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <label className="text-muted small">{t('common.status')}</label>
                    <div>
                      <span className={`badge bg-${quickViewIndicator.status === 'active' ? 'success' : 'secondary'}`}>
                        {quickViewIndicator.status === 'active' ? `✅ ${t('status.active')}` : `⏸️ ${t('status.inactive')}`}
                      </span>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <label className="text-muted small">{t('indicators.collectType')}</label>
                    <div>
                      <span className={`badge bg-${quickViewIndicator.collectType === 'Tự động' ? 'info' : 'warning'}`}>
                        {quickViewIndicator.collectType}
                      </span>
                    </div>
                  </div>
                  <div className="col-12">
                    <label className="text-muted small">{t('indicators.description')}</label>
                    <div>{quickViewIndicator.description || t('indicators.noDescription')}</div>
                  </div>
                  <div className="col-md-4">
                    <label className="text-muted small">{t('indicators.unit')}</label>
                    <div><strong>{quickViewIndicator.unit}</strong></div>
                  </div>
                  <div className="col-md-4">
                    <label className="text-muted small">{t('indicators.dataSource')}</label>
                    <div>{quickViewIndicator.dataSource}</div>
                  </div>
                  <div className="col-md-4">
                    <label className="text-muted small">{t('indicators.targetObject')}</label>
                    <div>{quickViewIndicator.targetObject}</div>
                  </div>
                </div>

                {/* Variables */}
                {quickViewIndicator.variables && quickViewIndicator.variables.length > 0 && (
                  <div className="mb-4">
                    <h6 className="border-bottom pb-2 mb-3">
                      <i className="bi bi-collection me-2"></i>{t('indicators.sourceVariables')} ({quickViewIndicator.variables.length})
                    </h6>
                    <div className="row g-2">
                      {quickViewIndicator.variables.map((v, idx) => (
                        <div key={idx} className="col-md-6">
                          <div className="card bg-light border-0">
                            <div className="card-body p-2">
                              <code className="text-primary">@{v.name}</code>
                              <div className="small text-muted">{v.label}</div>
                              <div className="small text-muted">
                                <i className="bi bi-database me-1"></i>
                                {v.source}.{v.table}.{v.field}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Processed Variables */}
                {quickViewIndicator.processedVariables && quickViewIndicator.processedVariables.length > 0 && (
                  <div className="mb-4">
                    <h6 className="border-bottom pb-2 mb-3">
                      <i className="bi bi-calculator me-2"></i>{t('indicators.processedFormulas')} ({quickViewIndicator.processedVariables.length})
                    </h6>
                    {quickViewIndicator.processedVariables.map((pv, idx) => (
                      <div key={idx} className="card bg-light border-0 mb-2">
                        <div className="card-body p-2">
                          <div><code className="text-success">@{pv.name}</code> = <strong>{pv.label}</strong></div>
                          <div className="small font-monospace text-muted">{pv.formula}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Thresholds */}
                {quickViewIndicator.thresholds && (
                  <div className="mb-4">
                    <h6 className="border-bottom pb-2 mb-3">
                      <i className="bi bi-exclamation-triangle me-2"></i>{t('indicators.thresholdRules')}
                    </h6>
                    <div className="row g-2">
                      {['high', 'medium', 'low', 'none'].map(level => {
                        const labels = { 
                          high: t('indicators.severityLevels.high'), 
                          medium: t('indicators.severityLevels.medium'), 
                          low: t('indicators.severityLevels.low'), 
                          none: t('indicators.severityLevels.none')
                        };
                        const colors = { high: 'danger', medium: 'warning', low: 'info', none: 'success' };
                        const threshold = quickViewIndicator.thresholds[level];
                        
                        return (
                          <div key={level} className="col-md-6">
                            <div className={`card border-${colors[level]}`}>
                              <div className={`card-header bg-${colors[level]} bg-opacity-10 py-2`}>
                                <small className={`text-${colors[level]} fw-bold`}>{labels[level]}</small>
                              </div>
                              <div className="card-body p-2">
                                {threshold?.conditions.map((cond, cidx) => (
                                  <div key={cidx} className="small">
                                    {cidx > 0 && <span className="badge bg-secondary me-1">{threshold.logic}</span>}
                                    <code>@{cond.variable}</code> <strong>{cond.operator}</strong> <code>{cond.value}</code>
                                  </div>
                                ))}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => setShowQuickViewModal(false)}>
                  Đóng
                </button>
                <button className="btn btn-primary" onClick={() => {
                  setShowQuickViewModal(false);
                  openEditModal(quickViewIndicator);
                }}>
                  <i className="bi bi-pencil me-1"></i>{t('common.edit')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal - Sẽ tiếp tục ở phần sau do độ dài */}
      {showModal && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)', overflowY: 'auto' }}>
          <div className="modal-dialog modal-xl" style={{ maxWidth: '90%' }}>
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {editingIndicator ? t('indicators.editIndicator') : t('indicators.createIndicator')}
                </h5>
                <button type="button" className="btn-close" onClick={() => setShowModal(false)}></button>
              </div>
              <form onSubmit={handleSave}>
                <div className="modal-body" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                  
                  {/* Basic Information */}
                  <h6 className="border-bottom pb-2 mb-3">
                    <i className="bi bi-info-circle me-2"></i>{t('indicators.basicInfo')}
                  </h6>
                  <div className="row g-3 mb-4">
                    <div className="col-md-6">
                      <label className="form-label">{t('indicators.name')} <span className="text-danger">*</span></label>
                      <input
                        type="text"
                        name="name"
                        className="form-control"
                        value={formData.name}
                        onChange={handleInputChange}
                        placeholder={t('indicators.namePlaceholder')}
                        required
                      />
                    </div>
                    <div className="col-md-3">
                      <label className="form-label">{t('indicators.category')} <span className="text-danger">*</span></label>
                      <select
                        name="category"
                        className="form-select"
                        value={formData.category}
                        onChange={handleInputChange}
                        required
                      >
                        <option value="Tài chính">{t('indicators.categories.financial')}</option>
                        <option value="Thanh toán">{t('indicators.categories.payment')}</option>
                        <option value="Hành vi">{t('indicators.categories.behavior')}</option>
                        <option value="Bên ngoài">{t('indicators.categories.external')}</option>
                        <option value="Khác">{t('indicators.categories.other')}</option>
                      </select>
                    </div>
                    <div className="col-md-3">
                      <label className="form-label">{t('indicators.unit')} <span className="text-danger">*</span></label>
                      <input
                        type="text"
                        name="unit"
                        className="form-control"
                        value={formData.unit}
                        onChange={handleInputChange}
                        placeholder={t('indicators.unitPlaceholder')}
                        required
                      />
                    </div>
                    <div className="col-md-12">
                      <label className="form-label">{t('indicators.description')}</label>
                      <textarea
                        name="description"
                        className="form-control"
                        rows="2"
                        value={formData.description}
                        onChange={handleInputChange}
                        placeholder={t('indicators.descriptionPlaceholder')}
                      />
                    </div>
                    <div className="col-md-3">
                      <label className="form-label">{t('indicators.collectType')} <span className="text-danger">*</span></label>
                      <select
                        name="collectType"
                        className="form-select"
                        value={formData.collectType}
                        onChange={handleInputChange}
                      >
                        <option value="Tự động">{t('indicators.collectTypes.automatic')}</option>
                        <option value="Thủ công">{t('indicators.collectTypes.manual')}</option>
                      </select>
                    </div>
                    <div className="col-md-3">
                      <label className="form-label">{t('indicators.targetObject')}</label>
                      <select
                        name="targetObject"
                        className="form-select"
                        value={formData.targetObject}
                        onChange={handleInputChange}
                      >
                        <option value="Khách hàng doanh nghiệp">{t('indicators.targetObjects.corporate')}</option>
                        <option value="Khách hàng cá nhân">{t('indicators.targetObjects.personal')}</option>
                        <option value="Tất cả">{t('indicators.targetObjects.all')}</option>
                      </select>
                    </div>
                    <div className="col-md-3">
                      <label className="form-label">{t('indicators.dataType')}</label>
                      <select
                        name="dataType"
                        className="form-select"
                        value={formData.dataType}
                        onChange={handleInputChange}
                      >
                        <option value="number">Number</option>
                        <option value="text">Text</option>
                        <option value="boolean">Boolean</option>
                        <option value="date">Date</option>
                      </select>
                    </div>
                    <div className="col-md-3">
                      <label className="form-label d-block">{t('indicators.options')}</label>
                      <div className="form-check form-switch">
                        <input
                          type="checkbox"
                          name="hasWeight"
                          className="form-check-input"
                          id="hasWeightSwitch"
                          checked={formData.hasWeight}
                          onChange={handleInputChange}
                        />
                        <label className="form-check-label" htmlFor="hasWeightSwitch">
                          <i className="bi bi-calculator me-1"></i>{t('indicators.canWeighted')}
                        </label>
                      </div>
                    </div>
                  </div>

                  {/* Data Source Selection - Professional Design */}
                  <h6 className="border-bottom pb-2 mb-3">
                    <i className="bi bi-database me-2"></i>{t('indicators.selectDataSource')}
                  </h6>
                  
                  <div className="row g-4 mb-4">
                    {/* Left Panel: Selection Form */}
                    <div className="col-lg-5">
                      <div className="card shadow-sm border-0 h-100">
                        <div className="card-header bg-gradient" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
                          <h6 className="mb-0">
                            <i className="bi bi-gear-fill me-2"></i>{t('indicators.configVariables')}
                          </h6>
                        </div>
                        <div className="card-body">
                          {/* Step 1: Data Source */}
                          <div className="mb-3">
                            <label className="form-label d-flex align-items-center">
                              <span className="badge rounded-pill bg-primary me-2" style={{ width: '24px', height: '24px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>1</span>
                              <span className="fw-semibold">{t('indicators.selectDataSourceLabel')}</span>
                            </label>
                            <Select
                              options={dataSourceOptions}
                              value={selectedSource}
                              onChange={handleDataSourceChange}
                              placeholder={`🗄️ ${t('indicators.selectSourcePlaceholder')}`}
                              isClearable
                              styles={{
                                control: (base, state) => ({
                                  ...base,
                                  borderRadius: '8px',
                                  borderWidth: '2px',
                                  borderColor: state.isFocused ? '#667eea' : '#e0e7ff',
                                  boxShadow: state.isFocused ? '0 0 0 3px rgba(102, 126, 234, 0.1)' : 'none',
                                  '&:hover': { borderColor: '#667eea' }
                                })
                              }}
                            />
                          </div>

                          {/* Step 2: Table */}
                          <div className="mb-3">
                            <label className="form-label d-flex align-items-center">
                              <span className="badge rounded-pill bg-primary me-2" style={{ width: '24px', height: '24px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>2</span>
                              <span className="fw-semibold">{t('indicators.selectDataTable')}</span>
                            </label>
                            <Select
                              options={tableOptions}
                              value={selectedTable}
                              onChange={setSelectedTable}
                              placeholder={`📊 ${t('indicators.selectTablePlaceholder')}`}
                              isClearable
                              isDisabled={!selectedSource}
                              styles={{
                                control: (base, state) => ({
                                  ...base,
                                  borderRadius: '8px',
                                  borderWidth: '2px',
                                  borderColor: state.isFocused ? '#667eea' : '#e0e7ff',
                                  boxShadow: state.isFocused ? '0 0 0 3px rgba(102, 126, 234, 0.1)' : 'none',
                                  '&:hover': { borderColor: '#667eea' }
                                })
                              }}
                            />
                          </div>

                          {/* Step 3: Field */}
                          <div className="mb-3">
                            <label className="form-label d-flex align-items-center">
                              <span className="badge rounded-pill bg-primary me-2" style={{ width: '24px', height: '24px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>3</span>
                              <span className="fw-semibold">{t('indicators.selectDataField')}</span>
                            </label>
                            <Select
                              options={fieldOptions}
                              value={selectedField}
                              onChange={setSelectedField}
                              placeholder={`🔖 ${t('indicators.selectFieldPlaceholder')}`}
                              isClearable
                              isDisabled={!selectedTable}
                              styles={{
                                control: (base, state) => ({
                                  ...base,
                                  borderRadius: '8px',
                                  borderWidth: '2px',
                                  borderColor: state.isFocused ? '#667eea' : '#e0e7ff',
                                  boxShadow: state.isFocused ? '0 0 0 3px rgba(102, 126, 234, 0.1)' : 'none',
                                  '&:hover': { borderColor: '#667eea' }
                                })
                              }}
                            />
                          </div>

                          <hr className="my-3" />

                          {/* Step 4: Variable Name */}
                          <div className="mb-3">
                            <label className="form-label d-flex align-items-center">
                              <span className="badge rounded-pill bg-success me-2" style={{ width: '24px', height: '24px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>4</span>
                              <span className="fw-semibold">{t('indicators.setVariableName')}</span>
                            </label>
                            <input
                              type="text"
                              className="form-control"
                              value={customVarName}
                              onChange={(e) => setCustomVarName(e.target.value)}
                              placeholder={t('indicators.variableNamePlaceholder', { count: formData.variables.length + 1 })}
                              disabled={!selectedField}
                              style={{ borderRadius: '8px', borderWidth: '2px', borderColor: '#e0e7ff' }}
                            />
                            <small className="text-muted d-block mt-2">
                              💡 {t('indicators.variableNameHint')}
                            </small>
                          </div>

                          {/* Add Button */}
                          <button
                            type="button"
                            className="btn btn-success w-100 py-2 fw-semibold"
                            onClick={handleAddVariable}
                            disabled={!selectedSource || !selectedTable || !selectedField}
                            style={{ borderRadius: '8px', fontSize: '16px' }}
                          >
                            <i className="bi bi-plus-circle-fill me-2"></i>{t('indicators.addVariable')}
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* Right Panel: Preview & Structure */}
                    <div className="col-lg-7">
                      <div className="card shadow-sm border-0 h-100">
                        <div className="card-header bg-light">
                          <div className="d-flex justify-content-between align-items-center">
                            <h6 className="mb-0">
                              <i className="bi bi-table me-2"></i>
                              {selectedTable ? t('indicators.structurePreview', { name: selectedTable.label }) : t('indicators.viewDataStructure')}
                            </h6>
                            {selectedSource && (
                              <button 
                                className="btn btn-sm btn-outline-primary"
                                onClick={() => {
                                  // View full structure
                                  const sourceData = availableDataSources.find(ds => ds.code === selectedSource.value);
                                  if (sourceData) {
                                    alert(t('indicators.sourceDetail', { 
                                      name: sourceData.name,
                                      description: sourceData.description,
                                      tableCount: sourceData.tables.length 
                                    }));
                                  }
                                }}
                              >
                                <i className="bi bi-eye me-1"></i>{t('indicators.viewDetail')}
                              </button>
                            )}
                          </div>
                        </div>
                        <div className="card-body" style={{ maxHeight: '500px', overflowY: 'auto' }}>
                          {!selectedSource ? (
                            <div className="text-center py-5 text-muted">
                              <i className="bi bi-database" style={{ fontSize: '48px', opacity: 0.3 }}></i>
                              <p className="mt-3">{t('indicators.selectSourceToView')}</p>
                            </div>
                          ) : !selectedTable ? (
                            <div>
                              <div className="alert alert-info">
                                <i className="bi bi-info-circle me-2"></i>
                                <strong>Nguồn:</strong> {selectedSource.label}
                                {selectedSource.description && (
                                  <div className="small mt-1">{selectedSource.description}</div>
                                )}
                              </div>
                              <h6 className="mb-3">{t('indicators.availableTables')}:</h6>
                              <div className="row g-2">
                                {availableDataSources.find(ds => ds.code === selectedSource.value)?.tables.map((table, idx) => (
                                  <div key={idx} className="col-md-6">
                                    <div 
                                      className="card border cursor-pointer" 
                                      style={{ cursor: 'pointer', transition: 'all 0.2s' }}
                                      onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
                                      onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
                                      onClick={() => setSelectedTable({ value: table.id, label: table.name })}
                                    >
                                      <div className="card-body py-2">
                                        <i className="bi bi-table me-2 text-primary"></i>
                                        <strong>{table.name}</strong>
                                        <div className="small text-muted">{table.fields.length} trường</div>
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          ) : (
                            <div>
                              <div className="alert alert-success bg-success bg-opacity-10 border-success">
                                <div className="d-flex justify-content-between align-items-start">
                                  <div>
                                    <i className="bi bi-check-circle me-2"></i>
                                    <strong>{t('indicators.viewingTable')}:</strong> {selectedTable.label}
                                  </div>
                                  <span className="badge bg-success">{fieldOptions.length} {t('indicators.fields')}</span>
                                </div>
                              </div>
                              
                              <h6 className="mb-3">{t('indicators.dataFieldList')}:</h6>
                              <div className="list-group">
                                {fieldOptions.map((field, idx) => {
                                  const typeColors = {
                                    number: 'success',
                                    text: 'info',
                                    date: 'warning',
                                    boolean: 'secondary'
                                  };
                                  const isSelected = selectedField?.value === field.value;
                                  return (
                                    <div 
                                      key={idx} 
                                      className={`list-group-item list-group-item-action ${isSelected ? 'active' : ''}`}
                                      style={{ 
                                        cursor: 'pointer',
                                        borderLeft: isSelected ? '4px solid #667eea' : 'none',
                                        transition: 'all 0.2s'
                                      }}
                                      onClick={() => setSelectedField(field)}
                                    >
                                      <div className="d-flex justify-content-between align-items-center">
                                        <div className="flex-grow-1">
                                          <div className="d-flex align-items-center mb-1">
                                            <code className={isSelected ? 'text-white' : 'text-dark'}>{field.label}</code>
                                            <span className={`badge bg-${typeColors[field.type] || 'dark'} ms-2`}>
                                              {field.type}
                                            </span>
                                          </div>
                                          {field.description && (
                                            <small className={isSelected ? 'text-white-50' : 'text-muted'}>
                                              {field.description}
                                            </small>
                                          )}
                                        </div>
                                        {isSelected && (
                                          <i className="bi bi-check-circle-fill text-white ms-2"></i>
                                        )}
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Variables List */}
                  {formData.variables.length > 0 && (
                    <div className="card shadow-sm border-0 mb-4">
                      <div className="card-header" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
                        <h6 className="mb-0">
                          <i className="bi bi-collection-fill me-2"></i>{t('indicators.selectedVariables')} ({formData.variables.length})
                        </h6>
                      </div>
                      <div className="card-body">
                        <div className="row g-3">
                          {formData.variables.map((variable, index) => (
                            <div key={index} className="col-md-6">
                              <div className="card border-start border-4 border-primary h-100" style={{ boxShadow: '0 2px 4px rgba(0,0,0,0.05)' }}>
                                <div className="card-body p-3">
                                  <div className="d-flex justify-content-between align-items-start mb-2">
                                    <div className="flex-grow-1">
                                      <div className="d-flex align-items-center mb-2">
                                        <code className="bg-light px-2 py-1 rounded text-primary" style={{ fontSize: '14px', fontWeight: '600' }}>
                                          @{variable.name}
                                        </code>
                                        <span className={`badge bg-${
                                          variable.fieldType === 'number' ? 'success' :
                                          variable.fieldType === 'text' ? 'info' :
                                          variable.fieldType === 'date' ? 'warning' :
                                          variable.fieldType === 'boolean' ? 'secondary' : 'dark'
                                        } ms-2`}>
                                          <i className={`bi bi-${
                                            variable.fieldType === 'number' ? '123' :
                                            variable.fieldType === 'text' ? 'fonts' :
                                            variable.fieldType === 'date' ? 'calendar3' :
                                            variable.fieldType === 'boolean' ? 'toggle-on' : 'question'
                                          } me-1`}></i>
                                          {variable.fieldType}
                                        </span>
                                      </div>
                                      <div className="fw-semibold text-dark mb-2">{variable.label}</div>
                                      <div className="d-flex align-items-center mb-2">
                                        <span className="badge bg-light text-dark border me-2">
                                          <i className={`bi bi-${
                                            variable.fieldType === 'number' ? '123' :
                                            variable.fieldType === 'text' ? 'fonts' :
                                            variable.fieldType === 'date' ? 'calendar3' :
                                            variable.fieldType === 'boolean' ? 'toggle-on' : 'question'
                                          } me-1`}></i>
                                          {t('indicators.type')}: {variable.fieldType === 'number' ? t('indicators.fieldTypes.number') : variable.fieldType === 'text' ? t('indicators.fieldTypes.text') : variable.fieldType === 'date' ? t('indicators.fieldTypes.date') : variable.fieldType === 'boolean' ? t('indicators.fieldTypes.boolean') : variable.fieldType}
                                        </span>
                                      </div>
                                      <div className="small text-muted">
                                        <i className="bi bi-database me-1"></i>
                                        {variable.source}.{variable.table}.{variable.field}
                                      </div>
                                      {variable.fieldDescription && (
                                        <div className="small text-muted mt-2 bg-light p-2 rounded">
                                          <i className="bi bi-info-circle me-1"></i>{variable.fieldDescription}
                                        </div>
                                      )}
                                    </div>
                                    <button
                                      type="button"
                                      className="btn btn-sm btn-outline-danger ms-2"
                                      onClick={() => handleRemoveVariable(index)}
                                      title={t('indicators.deleteVariable')}
                                    >
                                      <i className="bi bi-trash"></i>
                                    </button>
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Processed Variables / Calculated Fields */}
                  <h6 className="border-bottom pb-2 mb-3">
                    <i className="bi bi-calculator me-2"></i>{t('indicators.processedValues')}
                  </h6>
                  <div className="mb-4">
                    {formData.processedVariables.map((pVar, index) => (
                      <div key={index} className="card mb-3 border">
                        <div className="card-body">
                          <div className="row g-3">
                            <div className="col-md-3">
                              <label className="form-label">{t('indicators.variableName')}</label>
                              <input
                                type="text"
                                className="form-control"
                                value={pVar.name}
                                onChange={(e) => handleProcessedVariableChange(index, 'name', e.target.value)}
                                placeholder="value"
                                disabled={index === 0}
                              />
                            </div>
                            <div className="col-md-3">
                              <label className="form-label">{t('indicators.label')}</label>
                              <input
                                type="text"
                                className="form-control"
                                value={pVar.label}
                                onChange={(e) => handleProcessedVariableChange(index, 'label', e.target.value)}
                                placeholder={t('indicators.labelPlaceholder')}
                              />
                            </div>
                            <div className="col-md-5">
                              <label className="form-label">{t('indicators.formula')}</label>
                              <input
                                type="text"
                                className="form-control font-monospace"
                                value={pVar.formula}
                                onChange={(e) => handleProcessedVariableChange(index, 'formula', e.target.value)}
                                placeholder={t('indicators.formulaPlaceholder')}
                                list={`formula-suggestions-${index}`}
                              />
                              <datalist id={`formula-suggestions-${index}`}>
                                {/* Suggest individual variables */}
                                {formData.variables.map(v => (
                                  <option key={v.name} value={`@${v.name}`}>{v.label}</option>
                                ))}
                                {formData.processedVariables.filter((_, i) => i < index).map(pv => (
                                  <option key={pv.name} value={`@${pv.name}`}>{pv.label}</option>
                                ))}
                                {/* Suggest common formulas with 2 variables */}
                                {formData.variables.length >= 2 && (
                                  <>
                                    <option value={`(@${formData.variables[0].name} / @${formData.variables[1].name}) * 100`}>
                                      {t('indicators.formulaOptions.percentage', { var1: formData.variables[0].label, var2: formData.variables[1].label })}
                                    </option>
                                    <option value={`@${formData.variables[0].name} / @${formData.variables[1].name}`}>
                                      {t('indicators.formulaOptions.divide', { var1: formData.variables[0].label, var2: formData.variables[1].label })}
                                    </option>
                                    <option value={`@${formData.variables[0].name} + @${formData.variables[1].name}`}>
                                      {t('indicators.formulaOptions.add', { var1: formData.variables[0].label, var2: formData.variables[1].label })}
                                    </option>
                                    <option value={`@${formData.variables[0].name} - @${formData.variables[1].name}`}>
                                      {t('indicators.formulaOptions.subtract', { var1: formData.variables[0].label, var2: formData.variables[1].label })}
                                    </option>
                                    <option value={`@${formData.variables[0].name} * @${formData.variables[1].name}`}>
                                      {t('indicators.formulaOptions.multiply', { var1: formData.variables[0].label, var2: formData.variables[1].label })}
                                    </option>
                                  </>
                                )}
                              </datalist>
                            </div>
                            <div className="col-md-1">
                              <label className="form-label">&nbsp;</label>
                              {index > 0 && (
                                <button
                                  type="button"
                                  className="btn btn-outline-danger w-100"
                                  onClick={() => handleRemoveProcessedVariable(index)}
                                >
                                  <i className="bi bi-trash"></i>
                                </button>
                              )}
                            </div>
                          </div>
                          <small className="text-muted">
                            {t('indicators.variableReferenceHint')}
                          </small>
                        </div>
                      </div>
                    ))}
                    <button
                      type="button"
                      className="btn btn-outline-primary btn-sm"
                      onClick={handleAddProcessedVariable}
                    >
                      <i className="bi bi-plus-circle me-1"></i>{t('indicators.addProcessedVariable')}
                    </button>
                  </div>

                  {/* Thresholds / Alert Levels */}
                  <h6 className="border-bottom pb-2 mb-3">
                    <i className="bi bi-exclamation-triangle me-2"></i>{t('indicators.thresholdRules')}
                  </h6>
                  <div className="mb-4">
                        {['high', 'medium', 'low', 'none'].map((level) => {
                          const labels = {
                            high: t('indicators.severityLevels.high'),
                            medium: t('indicators.severityLevels.medium'),
                            low: t('indicators.severityLevels.low'),
                            none: t('indicators.severityLevels.none')
                          };
                          const colors = {
                            high: 'danger',
                            medium: 'warning',
                            low: 'info',
                            none: 'success'
                          };

                          return (
                            <div key={level} className="card mb-3 border">
                              <div className={`card-header bg-${colors[level]} bg-opacity-10`}>
                                <strong className={`text-${colors[level]}`}>{labels[level]}</strong>
                              </div>
                              <div className="card-body">
                                {formData.thresholds[level].conditions.map((condition, cIndex) => (
                                  <div key={cIndex} className="row g-2 mb-2 align-items-center">
                                    {cIndex > 0 && (
                                      <div className="col-md-1">
                                        <select
                                          className="form-select form-select-sm"
                                          value={formData.thresholds[level].logic}
                                          onChange={(e) => handleThresholdLogicChange(level, e.target.value)}
                                        >
                                          <option value="AND">AND</option>
                                          <option value="OR">OR</option>
                                        </select>
                                      </div>
                                    )}
                                    <div className={`col-md-${cIndex > 0 ? '3' : '4'}`}>
                                      <select
                                        className="form-select form-select-sm"
                                        value={condition.variable}
                                        onChange={(e) => handleThresholdConditionChange(level, cIndex, 'variable', e.target.value)}
                                      >
                                        <option value="">{t('indicators.selectVariable')}</option>
                                        {getAllVariableOptions().map(opt => (
                                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                                        ))}
                                      </select>
                                    </div>
                                    <div className="col-md-2">
                                      <select
                                        className="form-select form-select-sm"
                                        value={condition.operator}
                                        onChange={(e) => handleThresholdConditionChange(level, cIndex, 'operator', e.target.value)}
                                      >
                                        {condition.variable ? (
                                          getOperatorsForType(getVariableFieldType(condition.variable)).map(op => (
                                            <option key={op.value} value={op.value}>{op.label}</option>
                                          ))
                                        ) : (
                                          <>
                                            <option value=">=">&gt;=</option>
                                            <option value=">">&gt;</option>
                                            <option value="<=">&lt;=</option>
                                            <option value="<">&lt;</option>
                                            <option value="==">=</option>
                                            <option value="!=">!=</option>
                                          </>
                                        )}
                                      </select>
                                    </div>
                                    <div className="col-md-4">
                                      <input
                                        type="text"
                                        className="form-control form-control-sm"
                                        value={condition.value}
                                        onChange={(e) => handleThresholdConditionChange(level, cIndex, 'value', e.target.value)}
                                        placeholder={t('indicators.valueOrVariable')}
                                      />
                                    </div>
                                    <div className="col-md-2">
                                      <button
                                        type="button"
                                        className="btn btn-sm btn-outline-danger"
                                        onClick={() => handleRemoveThresholdCondition(level, cIndex)}
                                      >
                                        <i className="bi bi-trash"></i>
                                      </button>
                                    </div>
                                  </div>
                                ))}
                                <button
                                  type="button"
                                  className="btn btn-outline-secondary btn-sm mt-2"
                                  onClick={() => handleAddThresholdCondition(level)}
                                >
                                  <i className="bi bi-plus me-1"></i>{t('indicators.addCondition')}
                                </button>
                              </div>
                            </div>
                          );
                        })}
                      </div>

                  {/* Additional Fields */}
                  <h6 className="border-bottom pb-2 mb-3">
                    <i className="bi bi-gear me-2"></i>{t('indicators.additionalInfo')}
                  </h6>
                  <div className="row g-3">
                    <div className="col-md-6">
                      <label className="form-label">{t('indicators.validExample')}</label>
                      <input
                        type="text"
                        name="validExample"
                        className="form-control"
                        value={formData.validExample}
                        onChange={handleInputChange}
                        placeholder={t('indicators.validExamplePlaceholder')}
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label d-block">{t('indicators.options')}</label>
                      <div className="form-check form-switch">
                        <input
                          type="checkbox"
                          name="hasDefaultValue"
                          className="form-check-input"
                          id="hasDefaultValueSwitch"
                          checked={formData.hasDefaultValue}
                          onChange={handleInputChange}
                        />
                        <label className="form-check-label" htmlFor="hasDefaultValueSwitch">
                          <i className="bi bi-gear me-1"></i>{t('indicators.hasDefaultValue')}
                        </label>
                      </div>
                    </div>
                    {formData.hasDefaultValue && (
                      <div className="col-md-6">
                        <label className="form-label">{t('indicators.defaultValue')}</label>
                        <input
                          type="text"
                          name="defaultValue"
                          className="form-control"
                          value={formData.defaultValue}
                          onChange={handleInputChange}
                        />
                      </div>
                    )}
                    <div className="col-md-12">
                      <label className="form-label">{t('indicators.notes')}</label>
                      <textarea
                        name="notes"
                        className="form-control"
                        rows="3"
                        value={formData.notes}
                        onChange={handleInputChange}
                        placeholder={t('indicators.notesPlaceholder')}
                      />
                    </div>
                  </div>
                </div>
                <div className="modal-footer d-flex justify-content-between align-items-center">
                  <div className="alert alert-warning mb-0 py-2 px-3 flex-grow-1 me-3" style={{ maxWidth: '60%' }}>
                    <small>
                      <i className="bi bi-info-circle me-1"></i>
                      <strong>{t('common.note')}:</strong> {t('indicators.creationNotice')}
                    </small>
                  </div>
                  <div className="d-flex gap-2">
                    <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>
                      <i className="bi bi-x-circle me-1"></i>{t('common.cancel')}
                    </button>
                    <button type="submit" className="btn btn-primary">
                      <i className="bi bi-check-circle me-1"></i>
                      {editingIndicator ? t('common.update') : t('common.create')}
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Indicators;
